import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3/polaris';
import { parseSheet, readSpreadsheet } from '../../utils/spreadsheet';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { errorRowIndexes, RowErrors } from '../../utils/rowErrors';
// eslint-disable-next-line no-unused-vars
import { ApiError, SaveFailure } from '../../utils/serverErrors';
import { Messages, RowErrorSummary, SaveAlerts } from './ErrorMessages';
// eslint-disable-next-line no-unused-vars
import ListTable, { ListColumn, useScrollIntoView } from './ListTable';

/**
 * Creates a list of rows from an uploaded .xlsx file: offers the template, previews the rows with
 * their client and server errors, and creates the full set in one request. Used by the base rates
 * and TER group create pages.
 */

interface SpreadsheetUploadProps<R extends { key: number }, F extends string> {
  // Element ids start with this.
  idPrefix: string;
  title: string;
  description: string;
  templateUrl: string;
  // Columns the first row must name; also the order row errors are listed in.
  columns: readonly F[];
  fieldLabels: Record<F, string>;
  // Builds a row from the trimmed cell text of each column.
  toRow: (record: Record<F, string>) => R;
  // Must be stable between renders unless what it checks against changed.
  validate: (rows: R[]) => RowErrors<F>[];
  // Shown between the spreadsheet row number and the errors.
  previewColumns: ListColumn<R>[];
  // Adds a filter box to the preview, matching this text of each row.
  filterText?: (row: R) => string;
  // Orders the preview, which otherwise follows the spreadsheet; errors stay on their rows.
  compareRows?: (a: R, b: R) => number;
  filteringPlaceholder?: string;
  emptyMessage: string;
  // False until whatever validating and creating need has loaded.
  ready: boolean;
  // Rejects with the API error when the create fails.
  onCreate: (rows: R[]) => Promise<unknown>;
  describeSaveError: (error: ApiError, rowCount: number) => SaveFailure<F>;
  conflictHeader: string;
  onReload: () => void;
  // Shown above the template link, e.g. other fields the create needs.
  children?: React.ReactNode;
}

interface ParsedUpload<R, F extends string> {
  rows: R[];
  // Spreadsheet row number of each entry in `rows`.
  rowNumbers: number[];
  missingColumns: F[];
}

interface Item<R> {
  row: R;
  rowNumber: number;
  index: number;
}

const listInWords = (words: readonly string[]): string =>
  words.length < 2
    ? words.join('')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

function SpreadsheetUpload<R extends { key: number }, F extends string>({
  idPrefix,
  title,
  description,
  templateUrl,
  columns,
  fieldLabels,
  toRow,
  validate,
  previewColumns,
  filterText,
  filteringPlaceholder,
  compareRows,
  emptyMessage,
  ready,
  onCreate,
  describeSaveError,
  conflictHeader,
  onReload,
  children,
}: SpreadsheetUploadProps<R, F>): JSX.Element {
  const [upload, setUpload] = useState<ParsedUpload<R, F> | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [serverRowErrors, setServerRowErrors] = useState<RowErrors<F>[]>([]);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [conflict, setConflict] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [filteringText, setFilteringText] = useState('');
  const scrollIntoView = useScrollIntoView();

  const rowErrorsId = (index: number): string => `${idPrefix}UploadRowErrors-${index}`;

  const revealRow = (index: number): void => {
    setFilteringText('');
    scrollIntoView(rowErrorsId(index));
  };

  const rowErrors = useMemo(() => {
    const clientErrors = upload ? validate(upload.rows) : [];
    return clientErrors.map((errors, index) => ({ ...errors, ...serverRowErrors[index] }));
  }, [upload, validate, serverRowErrors]);

  const errorMessages = (errors: RowErrors<F>): string[] => [
    ...(errors.row ? [errors.row] : []),
    ...columns
      .filter(field => errors[field])
      .map(field => `${fieldLabels[field]}: ${errors[field]}`),
  ];

  const chooseFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files && event.target.files[0];
    // Browsers only fire change for a new value; clearing it lets a corrected file with the same name be read again.
    event.target.value = '';
    setUpload(null);
    setFileError(null);
    setServerRowErrors([]);
    setFormErrors([]);
    setConflict(null);
    setFilteringText('');
    if (!file) {
      return;
    }
    readSpreadsheet(file)
      .then(sheetRows => {
        const { records, rowNumbers, missingColumns } = parseSheet(sheetRows, columns);
        setUpload({ rows: records.map(toRow), rowNumbers, missingColumns });
      })
      .catch((error: Error) => setFileError(error.message));
  };

  const errorRows = errorRowIndexes(rowErrors);

  const create = (): void => {
    if (!upload) {
      return;
    }
    setSubmitting(true);
    setFormErrors([]);
    setConflict(null);
    // On success the page switches to the created rows and this form unmounts.
    onCreate(upload.rows).catch((error: ApiError) => {
      setSubmitting(false);
      const failure = describeSaveError(error, upload.rows.length);
      setServerRowErrors(failure.rowErrors);
      setFormErrors(failure.formErrors);
      setConflict(failure.conflict);
      const [first] = errorRowIndexes(failure.rowErrors);
      if (first !== undefined) {
        revealRow(first);
      }
    });
  };

  const canCreate =
    upload !== null &&
    upload.rows.length > 0 &&
    upload.missingColumns.length === 0 &&
    errorRows.length === 0 &&
    ready &&
    !submitting;

  const items = useMemo((): Item<R>[] => {
    if (!upload) {
      return [];
    }
    const parsed = upload.rows.map((row, index) => ({
      row,
      rowNumber: upload.rowNumbers[index],
      index,
    }));
    return compareRows ? parsed.sort((a, b) => compareRows(a.row, b.row)) : parsed;
  }, [upload, compareRows]);

  const tableColumns: ListColumn<Item<R>>[] = [
    { id: 'row', header: 'Row', cell: (item): number => item.rowNumber },
    ...previewColumns.map(column => ({
      id: column.id,
      header: column.header,
      cell: (item: Item<R>): React.ReactNode => column.cell(item.row),
    })),
    {
      id: 'errors',
      header: 'Errors',
      cell: (item): JSX.Element => (
        <Box id={rowErrorsId(item.index)} color="text-status-error">
          <Messages messages={errorMessages(rowErrors[item.index])} />
        </Box>
      ),
    },
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={description}
          actions={
            <Button
              id={`${idPrefix}Create`}
              variant="primary"
              loading={submitting}
              disabled={!canCreate}
              onClick={create}
            >
              Create
            </Button>
          }
        >
          {title}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {children}
        <a id={`${idPrefix}Template`} href={templateUrl} download>
          Download template
        </a>
        <FormField label="Spreadsheet (.xlsx)">
          <input
            id={`${idPrefix}File`}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={submitting}
            onChange={chooseFile}
          />
        </FormField>
        {fileError && (
          <Alert id={`${idPrefix}FileError`} type="error" header="Could not read the file">
            {fileError}
          </Alert>
        )}
        {upload && upload.missingColumns.length > 0 && (
          <Alert id={`${idPrefix}MissingColumns`} type="error" header="Missing columns">
            {`The first row must name the columns ${listInWords(
              columns
            )}. Missing: ${upload.missingColumns.join(', ')}.`}
          </Alert>
        )}
        {upload && upload.missingColumns.length === 0 && upload.rows.length === 0 && (
          <Alert id={`${idPrefix}EmptyUpload`} type="error">
            {emptyMessage}
          </Alert>
        )}
        <SaveAlerts
          idPrefix={idPrefix}
          conflictHeader={conflictHeader}
          conflict={conflict}
          formErrors={formErrors}
          onReload={onReload}
        />
        <RowErrorSummary
          idPrefix={idPrefix}
          rowNumbers={upload ? errorRows.map(index => upload.rowNumbers[index]) : []}
          onShowFirst={(): void => revealRow(errorRows[0])}
        />
        {items.length > 0 && (
          <ListTable
            id={`${idPrefix}Preview`}
            items={items}
            columns={tableColumns}
            filterText={filterText && ((item: Item<R>): string => filterText(item.row))}
            filteringPlaceholder={filteringPlaceholder}
            filtering={{ text: filteringText, onChange: setFilteringText }}
            trackBy={(item: Item<R>): string => String(item.row.key)}
            header={<Header variant="h3">{`Preview (${items.length})`}</Header>}
          />
        )}
      </SpaceBetween>
    </Container>
  );
}

export default SpreadsheetUpload;
